import { useTranslation } from 'react-i18next'

// This custom validator for react-hook-form is required for boolean fields
// because HTML inputs treat 'false' values as empty.
// See https://github.com/react-hook-form/react-hook-form/issues/2323

interface YearValidationInput {
	min: number
	max: number
	rangeMessage: string
	invalidMessage: string
	requiredMessage?: string
	isRequired?: boolean
}

type YearValidationValue = number | string | null | undefined

export const createYearRangeValidationRules = ({
	min,
	max,
	rangeMessage,
	invalidMessage,
	requiredMessage,
	isRequired = true,
}: YearValidationInput) => {
	const rules = {
		min: { value: min, message: rangeMessage },
		max: { value: max, message: rangeMessage },
		validate: (value: YearValidationValue) => {
			if (value === '' || value === null || value === undefined) {
				return isRequired ? (requiredMessage ?? 'This field is required') : true
			}
			const numericValue = Number(value)
			if (Number.isNaN(numericValue)) {
				return invalidMessage
			}
			if (numericValue < min || numericValue > max) {
				return rangeMessage
			}
			return true
		},
	} as {
		required?: boolean | string
		min: { value: number; message: string }
		max: { value: number; message: string }
		validate: (value: YearValidationValue) => boolean | string
	}

	if (isRequired) {
		rules.required = requiredMessage ?? true
	}

	return rules
}

export const useRequiredForBooleanRule = () => {
	const { t } = useTranslation()

	return {
		validate: (value: unknown) => {
			if (value === null || value === undefined) {
				return t('input.required')
			}
			return true
		},
	}
}
